/**
 * Variables that sit in a section several template categories can use, but which only resolve for
 * some of those categories. Keys are the lowercase inner key of the variable, values the template
 * category codes the variable is offered for.
 *
 * `paymentlink` is generated from a collection case, so only collection templates can use it.
 */
const CATEGORY_RESTRICTED_VARIABLES: Record<string, readonly string[]> = {
  paymentlink: ['collections'],
};

/** Variables that PaySuite onboarding has to be finished for; they cannot resolve to anything before that. */
const PAYMENT_LINK_VARIABLE_KEYS: readonly string[] = ['paymentlink'];

/** Strips the `<<>>` wrapping from a variable and lowercases it. */
export function toVariableKey(variable: string): string {
  return variable.replace(/^<<|>>$/g, '').toLowerCase();
}

/**
 * True unless the variable is restricted to categories that do not include the given category code,
 * or is a payment link variable while payment links are unavailable. An absent category code means
 * no category is selected, which carries no restriction anywhere else in the editor either.
 *
 * Payment links default to unavailable so a caller that cannot resolve the onboarding state never
 * offers a variable the API would refuse.
 */
export function isVariableAllowedForCategory(
  variable: string,
  categoryCode: string | undefined,
  paymentLinksAvailable = false,
): boolean {
  const key = toVariableKey(variable);
  if (!paymentLinksAvailable && PAYMENT_LINK_VARIABLE_KEYS.includes(key)) return false;

  const allowedCategories = CATEGORY_RESTRICTED_VARIABLES[key];
  if (!allowedCategories || !categoryCode) return true;
  return allowedCategories.includes(categoryCode.toLowerCase());
}

/** True when any of the texts uses a variable that needs payment links to be available. */
export function usesPaymentLinkVariable(...texts: (string | undefined)[]): boolean {
  return texts.some((text) =>
    PAYMENT_LINK_VARIABLE_KEYS.some((key) => text?.toLowerCase().includes(`<<${key}>>`) === true),
  );
}
